import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useTabVisibility } from 'mage-deps/@klarna/mp-ui'
import { selectors as coreSelectors } from 'mage-core'
import { useSelector } from 'react-redux'
import { createAction, handleActions } from 'redux-actions'

const requestData = ({ client, method, data, params }) => client.request({
  method,
  data,
  params
})

const INIT = 'data-provider/INIT'
const SUCCESS = 'data-provider/SUCCESS'
const FAILURE = 'data-provider/FAILURE'

const initRequest = createAction(INIT)
const widgetSuccess = createAction(SUCCESS)
const widgetFailure = createAction(FAILURE)

const dataProviderReducer = handleActions({
  [INIT]: state => ({
    ...state,
    isFetching: true,
    isError: false
  }),
  [SUCCESS]: (state, { payload }) => ({
    ...state,
    hasSuccessfulLoad: true,
    isFetching: false,
    isError: false,
    data: payload
  }),
  [FAILURE]: state => ({
    ...state,
    isFetching: false,
    isError: true
  })
}, {
  isFetching: false,
  isError: false
})

export default function DataProvider ({ backendUrl, method, query, params, isContentLoading, children, onLoading, onError, showLoaderOnInitialRequestOnly = false, autoRefreshInterval = false, cache = false }) {
  const [cachedData, setCachedData] = useState({})

  const client = useSelector(state => coreSelectors.clientFactory(state)(backendUrl))
  const isVisible = useTabVisibility()

  // This makes it 100% self contained. If we want it in the store we need to change this
  // bit and add the reducer to the main store reducers.
  const [state, dispatch] = React.useReducer(dataProviderReducer, {})
  const { data, isFetching, isError, hasSuccessfulLoad } = state

  useEffect(() => {
    let didCancel = false

    const fetchData = async () => {
      const cacheKey = `${backendUrl}_${JSON.stringify(query)}_${JSON.stringify(params)}`

      if (isVisible) {
        if (cache && cachedData[cacheKey]) {
          dispatch(widgetSuccess(cachedData[cacheKey]))
        } else {
          dispatch(initRequest())
        }

        try {
          const result = await requestData({ client, method, data: query, params })

          if (!didCancel) {
            dispatch(widgetSuccess(result.data))
            if (cache) {
              setCachedData({ ...cachedData, [cacheKey]: result.data })
            }
          }
        } catch (error) {
          if (!didCancel) {
            dispatch(widgetFailure())
          }
        }
      }
    }

    if (autoRefreshInterval) {
      const refreshInterval = setInterval(() => {
        fetchData()
      }, autoRefreshInterval * 1000)
      fetchData()
      return () => {
        didCancel = true
        clearInterval(refreshInterval)
      }
    } else {
      fetchData()
    }

    return () => {
      didCancel = true
    }
  }, [client, query, params, isVisible])

  const loadingValue = isFetching || isContentLoading
  // Only show loader the first time
  if (loadingValue && hasSuccessfulLoad && showLoaderOnInitialRequestOnly) {
    return children({ data, isLoading: loadingValue })
  }

  if (loadingValue && onLoading) {
    return onLoading()
  }
  if (isError && onError) {
    return onError()
  }
  return children({ data, isLoading: loadingValue, isError })
}

DataProvider.propTypes = {
  backendUrl: PropTypes.string.isRequired,
  method: PropTypes.oneOf(['GET', 'POST']).isRequired,
  query: PropTypes.object,
  params: PropTypes.object,
  isContentLoading: PropTypes.bool,
  children: PropTypes.func,
  autoRefreshInterval: PropTypes.number,
  onLoading: PropTypes.func,
  onError: PropTypes.func
}
